import type { ComboboxItem } from '@mantine/core';
import {
  Icon123,
  IconCalendarTime,
  IconCheckbox,
  IconForms,
  IconHash,
  IconList,
  IconSelector,
} from '@tabler/icons-react';
import { useCompanyConfiguration } from 'init/index';
import { i18nKeys, tKeysLoose, useTranslation } from 'locales';
import type { ReactNode } from 'react';
import { flat, isNullish, map, partition, pipe, prop } from 'remeda';

import type { CreateAutomationVariables } from '../../../api';

import type { AutomationFieldType } from './field-types';
import type { AutomationModel } from './types';

export type ComboboxItemWithIcon = ComboboxItem & { icon: ReactNode };

type _WizardField = {
  value: string;
  type: AutomationFieldType;
  canWrite: boolean;
  options?: string[]; // only for select fields
};

const INVOICE_FIELDS = [
  { value: 'billing_log_id', type: 'id', canWrite: true },
  { value: 'created_at', type: 'date', canWrite: false },
  { value: 'csv_id', type: 'id', canWrite: false },
  { value: 'currency', type: 'string', canWrite: false },
  { value: 'disputed_reason_id', type: 'id', canWrite: false },
  { value: 'due_date', type: 'date', canWrite: true },
  { value: 'external_id', type: 'string', canWrite: false },
  { value: 'issue_date', type: 'date', canWrite: false },
  { value: 'marked_as_to_be_processed', type: 'boolean', canWrite: false },
  {
    value: 'lifecycle_state',
    type: 'select',
    canWrite: false,
    options: ['paused', 'disputed'],
  },
  { value: 'paused_reason_id', type: 'id', canWrite: false },
  { value: 'recovery_plan_id', type: 'id', canWrite: false },
  { value: 'remaining_balance', type: 'number', canWrite: false },
  { value: 'remaining_late_fees', type: 'number', canWrite: false },
  { value: 'sales_category', type: 'string', canWrite: true },
  { value: 'total_tvac', type: 'number', canWrite: false },
  { value: 'user_id', type: 'id', canWrite: false },
] as const satisfies Array<_WizardField>;

const DEBTOR_FIELDS_ON_INVOICE = [
  { value: 'debtor__city', type: 'string', canWrite: false },
  { value: 'debtor__country_code', type: 'string', canWrite: false },
  { value: 'debtor__emails', type: 'array', canWrite: false },
  { value: 'debtor__locale', type: 'string', canWrite: false },
] as const satisfies Array<_WizardField>;

const DEBTOR_FIELDS = [
  { value: 'additional_line_1', type: 'string', canWrite: true },
  { value: 'allow_payment_plans', type: 'boolean', canWrite: true },
  { value: 'average_payment_delay', type: 'number', canWrite: false },
  { value: 'city', type: 'string', canWrite: true },
  { value: 'country_code', type: 'string', canWrite: true },
  { value: 'created_at', type: 'date', canWrite: false },
  { value: 'csv_id', type: 'id', canWrite: false },
  { value: 'currency', type: 'string', canWrite: false },
  { value: 'disputed_reason_id', type: 'id', canWrite: false },
  { value: 'disputed', type: 'boolean', canWrite: true },
  { value: 'emails', type: 'array', canWrite: true },
  { value: 'external_id', type: 'string', canWrite: false },
  { value: 'full_name', type: 'string', canWrite: true },
  { value: 'last_reminder_sent_date', type: 'date', canWrite: false },
  { value: 'locale', type: 'string', canWrite: true },
  { value: 'moral_entity', type: 'boolean', canWrite: true },
  { value: 'paused_reason_id', type: 'id', canWrite: false },
  { value: 'paused', type: 'boolean', canWrite: false },
  { value: 'recovery_plan_id', type: 'id', canWrite: false },
  { value: 'score', type: 'number', canWrite: false },
  { value: 'street_name', type: 'string', canWrite: true },
  { value: 'term_and_conditions_id', type: 'id', canWrite: true },
  { value: 'use_belgian_law', type: 'boolean', canWrite: false },
  { value: 'user_id', type: 'id', canWrite: false },
  { value: 'vat_number', type: 'string', canWrite: true },
  { value: 'zip_code', type: 'string', canWrite: true },
] as const satisfies Array<_WizardField>;

function useBaseFields() {
  const { t } = useTranslation();

  const transformField = (field: _WizardField, model: AutomationModel) => ({
    ...field,
    label: t(
      tKeysLoose.AUTOMATION.FIELDS[model.toUpperCase()][
        field.value.toUpperCase().replaceAll('_', '') as any
      ],
    ),
    icon: FIELD_TYPE_TO_ICON[field.type],
  });

  return {
    invoiceFields: INVOICE_FIELDS.map(field => transformField(field, 'invoice')),
    debtorFieldsOnInvoice: DEBTOR_FIELDS_ON_INVOICE.map(field => transformField(field, 'invoice')),
    debtorFields: DEBTOR_FIELDS.map(field => transformField(field, 'debtor')),
  } as const;
}

/** Returns the base fields and custom variables for a given model
 * formatted for use in a Mantine Select component
 */
export function useComputeFields(model: CreateAutomationVariables['targetModel']) {
  const invoiceFields = useComputeInvoiceFields();
  const debtorFields = useComputeDebtorFields();

  if (model === 'invoice') return invoiceFields;
  if (model === 'debtor') return debtorFields;
  throw new Error(`Unsupported model type: ${model}`);
}

function useComputeInvoiceFields() {
  const { t } = useTranslation();
  const { invoiceFields, debtorFieldsOnInvoice } = useBaseFields();
  const { custom_variables: customVariables } = useCompanyConfiguration();

  const [invoiceCustomVariables, debtorCustomVariables] = partition(
    customVariables,
    variable => variable.model_type === 'invoice',
  );

  return [
    {
      group: t(i18nKeys.AUTOMATION.WIZARD.INVOICE_FIELDS_GROUP),
      items: invoiceFields,
    },
    {
      group: t(i18nKeys.AUTOMATION.WIZARD.DEBTOR_FIELDS_GROUP),
      items: debtorFieldsOnInvoice,
    },
    {
      group: t(i18nKeys.AUTOMATION.WIZARD.INVOICE_CUSTOM_VARIABLES_GROUP),
      items: invoiceCustomVariables.map(variable => ({
        value: variable.column_name,
        label: variable.name,
        type: variable.var_type,
        icon: FIELD_TYPE_TO_ICON[variable.var_type],
        canWrite: true,
      })),
    },
    {
      group: t(i18nKeys.AUTOMATION.WIZARD.DEBTOR_CUSTOM_VARIABLES_GROUP),
      items: debtorCustomVariables.map(variable => ({
        value: `debtor__${variable.column_name}`,
        label: variable.name,
        type: variable.var_type,
        icon: FIELD_TYPE_TO_ICON[variable.var_type],
        canWrite: false,
      })),
    },
  ] as const;
}

function useComputeDebtorFields() {
  const { t } = useTranslation();
  const { debtorFields } = useBaseFields();
  const { custom_variables: customVariables } = useCompanyConfiguration();

  const debtorCustomVariables = customVariables.filter(
    variable => variable.model_type === 'debtor',
  );

  return [
    {
      group: t(i18nKeys.AUTOMATION.WIZARD.DEBTOR_FIELDS_GROUP),
      items: debtorFields,
    },
    {
      group: t(i18nKeys.AUTOMATION.WIZARD.DEBTOR_CUSTOM_VARIABLES_GROUP),
      items: debtorCustomVariables.map(variable => ({
        value: variable.column_name,
        label: variable.name,
        type: variable.var_type,
        icon: FIELD_TYPE_TO_ICON[variable.var_type],
        canWrite: true,
      })),
    },
  ] as const;
}

export function useGetFieldDefinition(
  fieldName: string | null,
  model: CreateAutomationVariables['targetModel'],
) {
  const fields = useComputeFields(model);
  if (isNullish(fieldName)) return null;

  const flattenedFields = pipe(fields, map(prop('items')), flat());
  const field = flattenedFields.find(f => f.value === fieldName);

  return field;
}

export const DISABLED_INPUT_FIELD_PROPS = {
  disabled: true,
  data: [],
} as const;

export const HIDDEN_INPUT_FIELD_PROPS = {
  disabled: true,
  data: [],
} as const;

export const ICON_PROPS = {
  size: 15,
  stroke: 1.5,
};

export const FIELD_TYPE_TO_ICON: Record<AutomationFieldType, React.ReactNode> = {
  id: <IconHash {...ICON_PROPS} />,
  number: <Icon123 {...ICON_PROPS} />,
  string: <IconForms {...ICON_PROPS} />,
  boolean: <IconCheckbox {...ICON_PROPS} />,
  date: <IconCalendarTime {...ICON_PROPS} />,
  select: <IconSelector {...ICON_PROPS} />,
  array: <IconList {...ICON_PROPS} />,
};
